<template>
  <div class="h-full flex flex-col items-center py-8 px-4">
    <div class="mobile-padding"></div>
    <div class="liquidGlassCard rounded-3xl w-full h-full pb-6 pt-14 flex flex-col">
      <button class="absolute w-8 h-8 left-5 top-5 " @click="$router.back">
        <ColorImg
          src="/images/icons/back.png"
          class="bg-current w-full h-full"
          alt=""
        />
      </button>
      <div class="absolute w-8 h-8 right-5 top-5 " @click="refreshPurchases">
        <ColorImg
          src="/images/icons/refresh.png"
          class="bg-current w-full h-full text-primary"
          alt=""
        />
      </div>
      <div class="absolute left-16 text-2xl top-5 title-color ">Mes achats</div>
      
      <div class="px-4 overflow-y-auto flex-1 h-full">
        <DataFetcher dataKey="riderProducts" ref="productsGetter" @fetched="onProductsFetched">
          <template #any="{ loading: productsLoading, data: productsData }">
            <DataFetcher dataKey="riderPurchases" ref="purchasesGetter">
              <template #any="{ loading: purchasesLoading, loadingError, refresh, data }">
            <div v-if="productsLoading || purchasesLoading" class="flex flex-col gap-4">
              <div v-for="i in 3" :key="i" class="shimmerContainer h-40 rounded-2xl w-full"></div>
            </div>
            
            <div v-else-if="loadingError" class="flex flex-col items-center justify-center p-8 text-center h-full">
              <NoDataView :refresh="refresh" />
            </div>
            
            <div v-else-if="!data || data.length === 0" class="flex flex-col items-center justify-center p-12 text-center h-[60vh]">
              <div class="w-32 h-32 mb-6 opacity-20 mx-auto">
                <ColorImg src="/images/icons/shopping-bag.svg" class="w-full h-full bg-current" />
              </div>
              <div class="text-xl font-bold title-color">Vous n'avez effectué aucun achat</div>
              <p class="text-gray-500 mt-2">Vos commandes d'équipement s'afficheront ici.</p>
              <button @click="$router.push('/shop')" class="mt-8 bg-primary text-white px-8 py-3 rounded-full font-bold shadow-lg shadow-primary/30">Aller à la boutique</button>
            </div>
            
            <div v-else class="space-y-4">
              <div 
                v-for="purchase in data" 
                :key="purchase.id"
                class="bg-white/50 backdrop-blur-md rounded-2xl p-4 border border-white/40 shadow-sm flex flex-col gap-4"
              >
                <!-- Header -->
                <div class="flex justify-between items-start mb-2">
                  <div class="flex-1 flex gap-3">
                    <div v-if="productsMap[purchase.productId]?.image" class="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200">
                      <img :src="productsMap[purchase.productId]?.image" class="w-full h-full object-cover" />
                    </div>
                    <div>
                      <div class="font-bold title-color text-lg">{{ productsMap[purchase.productId]?.name || purchase.productName || 'Produit' }}</div>
                      <div class="text-xs text-gray-500 font-medium">Date : {{ formatDate(purchase.createdAt) }}</div>
                    </div>
                  </div>
                  <div
                    :class="getStatusClass(purchase.status)"
                    class="px-3 py-1 rounded-full text-xs font-bold text-white shadow-sm whitespace-nowrap"
                  >
                    {{ getStatusLabel(purchase.status) }}
                  </div>
                </div>

                <!-- Body -->
                <div class="grid grid-cols-2 gap-2 my-2 bg-white/40 p-3 rounded-xl">
                  <div>
                    <div class="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Quantité</div>
                    <div class="font-bold text-gray-800">{{ purchase.quantity }}</div>
                  </div>
                  <div class="text-right">
                    <div class="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Total</div>
                    <div class="font-black text-primary">{{ formatPrice(purchase.totalPrice) }}</div>
                  </div>
                </div>

                <!-- Actions -->
                <div class="flex justify-end items-center gap-3 mt-2">
                  <button 
                    @click="viewDetails(purchase)"
                    class="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm font-bold shadow-sm hover:bg-gray-50 transition"
                  >
                    Détails
                  </button>
                  <button 
                    v-if="! ['confirmed', 'expired'].includes(purchase.status)"
                    @click="payNow(purchase)"
                    class="bg-secondary text-white px-4 py-2 rounded-xl text-sm font-bold shadow-sm hover:bg-secondary-600 transition"
                  >
                    Payer
                  </button>
                </div>
              </div>
            </div>
          </template>
        </DataFetcher>
      </template>
    </DataFetcher>
  </div>
    </div>
    <ProductPurchasePaymentModal />
    <ProductPurchaseDetailsModal />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import ColorImg from '@/presentation/components/img/ColorImg.vue'
import DataFetcher from '@/features/data/presentation/components/DataFetcher.vue'
import NoDataView from '@/features/data/presentation/components/NoDataView.vue'
import Events from '@/events/events'
import ProductPurchasePaymentModal from './components/ProductPurchasePaymentModal.vue'
import ProductPurchaseDetailsModal from './components/ProductPurchaseDetailsModal.vue'

const purchasesGetter = ref(null)
const productsGetter = ref(null)
const products = ref([])
let paymentConfirmedHandler = null

const productsMap = computed(() => {
  const map = {}
  products.value.forEach(p => { map[p.id] = p })
  return map
})

function onProductsFetched({ data }) {
  products.value = data || []
}

onMounted(() => {
  paymentConfirmedHandler = Events.handle('product_purchase_payment_confirmed', () => {
    refreshPurchases()
  })
})

function refreshPurchases() {
  if (purchasesGetter.value) {
    if (typeof purchasesGetter.value.refresh === 'function') {
      purchasesGetter.value.refresh()
    }
  }
  if (productsGetter.value) {
    if (typeof productsGetter.value.refresh === 'function') {
      productsGetter.value.refresh()
    }
  }
}

onUnmounted(() => {
  if (paymentConfirmedHandler) {
    Events.removeHandler(paymentConfirmedHandler)
  }
})

function formatPrice(price) {
  if (!price) return '0 FCFA'
  return new Intl.NumberFormat('fr-BJ', {
    style: 'currency',
    currency: 'XOF',
    maximumFractionDigits: 0
  }).format(price)
}

function formatDate(timestamp) {
  if (!timestamp) return '-'
  const date = new Date(timestamp)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getStatusLabel(status) {
  const labels = {
    non_paye: 'Non payé',
    paye: 'Payé',
    en_cours_de_livraison: 'En livraison',
    delivre: 'Livré',
  }
  return labels[status] || status
}

function getStatusClass(status) {
  const classes = {
    non_paye: 'bg-yellow-500',
    paye: 'bg-green-500',
    en_cours_de_livraison: 'bg-blue-500',
    delivre: 'bg-primary-500',
  }
  return classes[status] || 'bg-gray-500'
}

function viewDetails(purchase) {
  const productName = productsMap.value[purchase.productId]?.name || purchase.productName || 'Produit'
  Events.dispatch('open_purchase_details_modal', {
    ...purchase,
    productName
  })
}

function payNow(purchase) {
  console.log('💎 Dispatching payment modal for:', purchase)
  const productName = productsMap.value[purchase.productId]?.name || purchase.productName || 'Produit'
  Events.dispatch('show_product_purchase_payment_modal', {
    product: { name: productName, price: purchase.unitPrice, id: purchase.productId },
    quantity: purchase.quantity,
    purchaseId: purchase.id,
    phone: purchase.phone || '',
    ...purchase
  })
}



</script>

<script>
export default {
  name: 'MyPurchases'
}
</script>
